// Nsofar Sync Panel — shows per-patient sync status, errors, and manual re-trigger.

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { formatGenericRef, formatPatientRef } from "../lib/refs.js";
import { Shell } from "../components/Shell.js";

type SyncStatus = "synced" | "pending" | "failed" | "not_tagged";

interface NsofarSyncEntry {
  _id: string;
  full_name?: string | null;
  name?: string | null;
  nsofar_patient_id?: string | null;
  nsofar_tagged: boolean;
  sync_status: SyncStatus;
  last_sync_attempt: string | null;
  last_sync_error: string | null;
  pending_sr_count: number;
}

const STATUS_BADGE: Record<SyncStatus, string> = {
  synced:     "bg-green-100 text-green-700",
  pending:    "bg-amber-100 text-amber-700",
  failed:     "bg-red-100 text-red-700",
  not_tagged: "bg-slate-100 text-slate-500",
};

// Pagination envelope: /patients returns { data, total, page, limit }, not a
// raw array. Without the `select` transformer the component would call
// `.filter` on the envelope object and crash at runtime even though
// useQuery<NsofarSyncEntry[]> compiled cleanly.
//
// TODO(separate task): the page expects NsofarSyncEntry shape with fields
// like sync_status, last_sync_attempt, last_sync_error, pending_sr_count —
// but /patients returns IPatient docs that do not have any of those fields.
// Until a real /nsofar-sync-panel endpoint exists those fields will read as
// `undefined`, the STATUS_BADGE lookup will return undefined, and the page
// will load but render uninformative rows. The envelope unwrap below is a
// crash fix only; the data-shape mismatch is a separate ticket.
type PatientsListEnvelope = { data: NsofarSyncEntry[]; total: number; page: number; limit: number };

function useSyncPanel(): ReturnType<typeof useQuery<PatientsListEnvelope, Error, NsofarSyncEntry[]>> {
  return useQuery<PatientsListEnvelope, Error, NsofarSyncEntry[]>({
    queryKey: ["nsofar-sync-panel"],
    queryFn: () => apiFetch<PatientsListEnvelope>("/patients?nsofar_tagged=true&limit=100"),
    select: (res) => res.data,
    refetchInterval: 30_000,
  });
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
function SyncRow({ entry, onRetrigger }: { entry: NsofarSyncEntry; onRetrigger: (id: string) => void }): JSX.Element {
  const lastAttempt = entry.last_sync_attempt ? new Date(entry.last_sync_attempt).toLocaleString() : "—";

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-3 px-4">
        <p className="text-sm font-medium text-slate-900">{formatPatientRef(entry)}</p>
        <p className="text-xs text-slate-400 font-mono">{formatGenericRef(entry._id ?? entry.nsofar_patient_id, "P")}</p>
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 font-mono">
        {entry.nsofar_patient_id ?? "—"}
      </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[entry.sync_status] ?? "bg-slate-100 text-slate-500"}`}>
          {entry.sync_status?.replace("_", " ") ?? "unknown"}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-slate-500">{lastAttempt}</td>
      <td className="py-3 px-4">
        {entry.last_sync_error ? (
          <p className="text-xs text-red-600 max-w-xs truncate" title={entry.last_sync_error}>
            {entry.last_sync_error}
          </p>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="py-3 px-4 text-right">
        {entry.pending_sr_count > 0 && (
          <span className="inline-block mr-3 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
            {entry.pending_sr_count} pending
          </span>
        )}
        {entry.nsofar_tagged && entry.sync_status !== "synced" && (
          <button
            onClick={() => onRetrigger(entry._id)}
            className="text-xs text-navy-700 hover:text-navy-900 font-medium"
          >
            Re-sync
          </button>
        )}
      </td>
    </tr>
  );
}

// eslint-disable-next-line max-lines-per-function -- coherent React component / single-purpose hook; splitting would shuffle lines without semantic gain. Tracked under TASK-N19 Phase B.
export function NsofarSyncPanelPage(): JSX.Element {
  const { data: entries, isLoading, isError, refetch } = useSyncPanel();
  const qc = useQueryClient();
  const [retriggerResult, setRetriggerResult] = useState<string | null>(null);

  const retrigger = useMutation({
    mutationFn: (patientId: string) =>
      apiFetch(`/patients/${patientId}/nsofar-sync`, { method: "POST", body: "{}" }),
    onSuccess: (_data, patientId) => {
      setRetriggerResult(`Re-sync queued for patient ${formatGenericRef(patientId, "P")}`);
      void qc.invalidateQueries({ queryKey: ["nsofar-sync-panel"] });
      setTimeout(() => setRetriggerResult(null), 4000);
    },
  });

  const tagged = entries?.filter((e) => e.nsofar_tagged) ?? [];

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Nsofar Sync Panel</h1>
            {!isLoading && (
              <p className="text-xs text-slate-500 mt-0.5">
                {tagged.length} tagged patients
              </p>
            )}
          </div>
          <button
            onClick={() => void refetch()}
            className="text-sm text-navy-700 hover:text-navy-900 font-medium"
          >
            Refresh
          </button>
        </div>

        <div role="note" className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <p className="font-medium">Sync status data is not yet available.</p>
          <p className="text-xs mt-1 text-amber-700">
            This view currently lists Nsofar-tagged patients but cannot show real sync status, last attempt, or errors &mdash; those fields require a dedicated <code className="font-mono text-xs">/nsofar-sync-panel</code> backend endpoint that has not yet shipped. Re-sync triggers still work for tagged patients.
          </p>
        </div>

        {retriggerResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm text-green-700">
            {retriggerResult}
          </div>
        )}

        {isLoading && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}

        {isError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
            <p className="text-sm text-red-700 font-medium">Failed to load Nsofar sync data.</p>
          </div>
        )}

        {!isLoading && !isError && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Patient</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nsofar ID</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Last Attempt</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Error</th>
                  <th className="py-2.5 px-4" />
                </tr>
              </thead>
              <tbody>
                {tagged.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-400">No Nsofar-tagged patients.</td></tr>
                )}
                {tagged.map((entry) => (
                  <SyncRow
                    key={entry._id}
                    entry={entry}
                    onRetrigger={(id) => retrigger.mutate(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}
